'use client';
import styled from '@emotion/styled';
import { Box, Typography } from '@mui/material';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { ComponentProps } from 'react';

const MainDiv = styled.div`
  height: auto;
  width: 100%;
  padding: 13% 12.5% calc(10.51% - 32px) 12.5%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: ${({ theme }) => theme.palette.common.midnight};
  @media (max-width: 768px) {
    padding: 98px 20px;
    min-height: 100vh;
  }
`;

const Heading = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 40px;
  line-height: 112.3%;
  align-items: center;
  color: #ffffff;
  margin-bottom: 32px;
  min-width: 409px;
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 20px;
    margin-top: 10px;
    min-width: unset;
  }
`;

const SubHeading = styled(Typography)`
  font-size: 28px;
  line-height: 112.3%;
  align-items: center;
  color: #ffffff;
  margin-bottom: 25px;
  min-width: 409px;
  margin-top: 30px;
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 18px;
    margin-top: 10px;
    min-width: unset;
  }
`;

const Text = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 160%;
  color: #ffffff;
  margin-bottom: 15px;
  & a {
    color: #4497aa;
    text-decoration: none;
  }
`;

const HeaderImage = styled(Image)`
  width: 500px;
  max-width: 100%;
  border-radius: 30px;
  margin-bottom: 50px;
  transform: rotate(-2deg);
`;

const List = styled.ul`
  list-style-type: none;
`;
List.defaultProps = {
  className: 'career-list',
};

const ListItem = styled.li`
  position: relative;
  color: white;
  font-size: 16px;
  &::before {
    content: '\2022';
    color: white;
    position: absolute;
    left: -25px;
    top: -8px;
    font-size: 30px;
  }
  p {
    margin-bottom: 15px;
  }
`;

interface CareersProps {
  careers: {
    _id: string;
    _type: 'jobListing';
    _rev: string;
    _updatedAt: string;
    _createdAt: string;
    title: string;
    body: ComponentProps<typeof PortableText>['value'];
    link?: string;
  }[];
}

const Careers = (props: CareersProps) => {
  console.log('Careers component props:', props);
  return (
    <MainDiv>
      <HeaderImage
        src="/careers/contribute-social-change.jpg"
        alt="Person standing holding Curiko Flyer"
        width={500}
        height={(500 / 1200) * 800}
      />
      <Box mb={8}>
        <SubHeading>Available Positions:</SubHeading>
        <List>
          {props.careers.map(career => (
            <ListItem key={career._id}>
              <Text>
                <a
                  href={`#job-${career._id}`}
                  style={{ color: 'white', textDecoration: 'underline' }}
                >
                  {career.title}
                </a>
              </Text>
            </ListItem>
          ))}
        </List>
      </Box>
      {props.careers.map(career => (
        <Box key={career._id} mb={8} id={`job-${career._id}`}>
          <Heading variant="h1">{career.title}</Heading>
          <PortableText
            value={career.body}
            components={{
              block: {
                h1: ({ children }) => <Heading variant="h1">{children}</Heading>,
                h2: ({ children }) => <SubHeading variant="h2">{children}</SubHeading>,
                h3: ({ children }) => <SubHeading variant="h3">{children}</SubHeading>,
                h4: ({ children }) => <SubHeading variant="h4">{children}</SubHeading>,
                h5: ({ children }) => <SubHeading variant="h5">{children}</SubHeading>,
                h6: ({ children }) => <SubHeading variant="h6">{children}</SubHeading>,
                normal: ({ children }) => <Text>{children}</Text>,
              },
              list: ({ children }) => <List>{children}</List>,
              listItem: ({ children }) => <ListItem>{children}</ListItem>,
            }}
          />
        </Box>
      ))}
    </MainDiv>
  );
};

export default Careers;
